import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scale } from "lucide-react";
import Hero from "../components/Hero";
import {
  FIRM,
  PRACTICE_AREAS,
  ATTORNEYS,
  CHARLESTON_IMAGE,
  FOUNDERS_IMAGE,
} from "../mock";

const Home = () => {
  return (
    <div className="bg-white">
      <Hero />

      {/* Tagline strip */}
      <section className="bg-nw-cream py-16 md:py-20">
        <div className="container-nw grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="text-nw-gold text-xs uppercase tracking-[0.35em] mb-4">
              The Firm
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-nw-navy leading-tight">
              A boutique built on{" "}
              <span className="italic">experience</span> and{" "}
              <span className="italic">perspective</span>.
            </h2>
            <div className="h-0.5 w-16 bg-nw-gold mt-6" />
          </div>
          <div className="md:col-span-7 text-nw-navy/80 text-base md:text-lg leading-relaxed space-y-4">
            <p>
              Founded by Derek Newberry and Elizabeth Wieters, Newberry Wieters
              combines decades of large-firm experience with the responsiveness
              and client-focused approach of a boutique law firm. We have the
              experience to understand how both sides think, which allows us to
              implement an effective, individually tailored approach toward the
              result you seek.
            </p>
          </div>
        </div>
      </section>

      {/* Founders duo */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-nw grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative">
            <div className="relative aspect-[3/4] overflow-hidden bg-nw-stone">
              <img
                src={FOUNDERS_IMAGE}
                alt="Derek Newberry and Elizabeth Wieters"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 hidden md:block w-32 h-32 border-2 border-nw-gold pointer-events-none" />
            <div className="absolute -top-5 -left-5 hidden md:block w-24 h-24 border-2 border-nw-navy/20 pointer-events-none" />
          </div>
          <div className="md:col-span-6">
            <div className="text-nw-gold text-xs uppercase tracking-[0.35em] mb-4">
              Meet the Founders
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-nw-navy leading-tight">
              Derek Newberry &amp; Elizabeth Wieters
            </h2>
            <div className="h-0.5 w-16 bg-nw-gold mt-6 mb-6" />
            <p className="text-nw-navy/80 text-base md:text-lg leading-relaxed">
              Two Charleston attorneys who spent most of their careers with a
              large national firm helping clients navigate high-stakes, complex
              disputes across South Carolina. Newberry Wieters was built to
              keep operating in that same space while also providing every
              client the senior-level, client-focused attention only a
              boutique can deliver.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {ATTORNEYS.map((a) => (
                <Link
                  key={a.slug}
                  to={`/attorneys/${a.slug}`}
                  className="group block border-l-2 border-nw-gold pl-4 hover:bg-nw-cream transition-colors py-2 -my-2 pr-2"
                >
                  <div className="text-nw-gold text-[10px] uppercase tracking-[0.3em] mb-1">
                    {a.title}
                  </div>
                  <div className="font-serif-display text-2xl text-nw-navy group-hover:text-nw-gold transition-colors">
                    {a.firstName} {a.lastName}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-nw-navy/60 group-hover:text-nw-gold">
                    View Profile
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Attorneys */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-nw">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div>
              <div className="text-nw-gold text-xs uppercase tracking-[0.35em] mb-4">
                Our Attorneys
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-nw-navy">
                Meet the founding partners
              </h2>
              <div className="h-0.5 w-16 bg-nw-gold mt-6" />
            </div>
            <Link
              to="/attorneys"
              className="inline-flex items-center gap-2 text-nw-navy hover:text-nw-gold text-xs font-bold uppercase tracking-[0.15em] link-underline"
            >
              View All Attorneys <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {ATTORNEYS.filter((a) => a.title && a.title.toLowerCase().includes("partner")).map((a) => (
              <Link
                key={a.slug}
                to={`/attorneys/${a.slug}`}
                className="group block"
              >
                <div className="relative overflow-hidden bg-nw-stone aspect-[4/5]">
                  <img
                    src={a.photo}
                    alt={`${a.firstName} ${a.lastName}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-nw-navy-dark/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
                    <div className="text-nw-gold-light text-xs uppercase tracking-[0.3em] mb-2">
                      {a.title}
                    </div>
                    <div className="font-serif-display text-3xl lg:text-4xl">
                      {a.firstName} {a.lastName}
                    </div>
                    <div className="mt-3 text-white/80 text-sm">
                      {a.practiceAreas.slice(0, 4).join(" \u2022 ")}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/attorneys"
              className="inline-flex items-center gap-2 text-nw-navy hover:text-nw-gold text-xs font-bold uppercase tracking-[0.15em] link-underline"
            >
              View The Full Team <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Practice areas grid */}
      <section className="py-20 md:py-24 bg-nw-cream">
        <div className="container-nw">
          <div className="max-w-2xl mb-14">
            <div className="text-nw-gold text-xs uppercase tracking-[0.35em] mb-4">
              Practice Areas
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-nw-navy">
              A full-service litigation practice
            </h2>
            <div className="h-0.5 w-16 bg-nw-gold mt-6" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-nw-stone border border-nw-stone">
            {PRACTICE_AREAS.map((p) => (
              <Link
                key={p.slug}
                to={`/practice-areas#${p.slug}`}
                className="group bg-white hover:bg-nw-navy transition-colors duration-300 p-7 lg:p-8 flex flex-col justify-between min-h-[180px]"
              >
                <div>
                  <Scale
                    strokeWidth={1.2}
                    className="w-6 h-6 text-nw-gold mb-4"
                  />
                  <h3 className="font-serif-display text-xl text-nw-navy group-hover:text-white transition-colors">
                    {p.title}
                  </h3>
                </div>
                <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-nw-gold group-hover:text-nw-gold-light">
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${CHARLESTON_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-nw-navy-dark/92" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative container-nw text-center max-w-3xl mx-auto text-white">
          <div className="text-nw-gold-light text-xs uppercase tracking-[0.35em] mb-5">
            {FIRM.address.city}, South Carolina
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-tight">
            Ready to talk about your matter?
          </h2>
          <div className="h-0.5 w-20 bg-nw-gold mx-auto mt-6 mb-8" />
          <div className="mt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 bg-nw-gold hover:bg-nw-gold-light text-nw-navy-dark px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] transition-all"
            >
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={`tel:${FIRM.phone}`}
              className="inline-flex items-center gap-2 border border-white/60 hover:bg-white hover:text-nw-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] transition-all"
            >
              Call {FIRM.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
